<template>
    <MPModal
        v-model="isOpen"
        variant="center"
        size="md"
        title="Edit file"
    >
        <form
            class="form-col relative flex w-full"
            @submit="onSubmit"
        >
            <div class="form-titled-item">
                <span class="text-muval-gray-1 text-sm font-medium">Label</span>
                <MuvalInput
                    vid="display_label"
                    placeholder="Enter display label"
                    :maxlength="255"
                />
            </div>
            <div class="form-titled-item">
                <span class="text-muval-gray-1 text-sm font-medium">Tags</span>
                <MPTags
                    vid="tags"
                    placeholder="Add tags..."
                />
                <p class="text-muval-gray-3 text-xs">Press Enter to add a tag.</p>
            </div>
            <div
                v-if="shareable"
                class="flex items-center gap-x-2"
            >
                <MuvalCheckbox vid="shared_with_field_staff" />
                <span class="text-muval-gray-1 text-sm font-medium">Share with field staff</span>
            </div>
        </form>

        <template #footer>
            <div class="flex flex-col justify-end gap-2 md:flex-row">
                <MuvalButton
                    cancelBtn
                    class="order-2 md:order-1"
                    type="button"
                    @click="handleCancel"
                >
                    Cancel
                </MuvalButton>
                <!--
                    Submitted by click rather than by `form=`: MuvalButton's root
                    is a wrapper element, so a `form` attribute lands there and
                    never reaches the button inside it.
                -->
                <MuvalButton
                    :loading="isSubmitting"
                    class="order-1 md:order-2"
                    @click="onSubmit"
                >
                    Save
                </MuvalButton>
            </div>
        </template>
    </MPModal>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate';
import { toast } from 'vue-sonner';
import MuvalButton from '@buttons/MuvalButton.vue';
import MuvalCheckbox from '@components/inputs/MuvalCheckbox.vue';
import MPModal from '@components/modal/MPModal.vue';
import { MuvalInput } from '@inputs';
import MPTags from '@inputs/MPTags.vue';

/**
 * Renaming and re-tagging a stored file, shared by every file list.
 *
 * The caller supplies only the write: `save` is awaited, and a rejection with a
 * Laravel validation body is mapped back onto the fields.
 */

export interface FileEditValues {
    display_label: string | null;
    tags: string[];
    shared_with_field_staff: boolean;
}

const props = withDefaults(
    defineProps<{
        file: Record<string, any> | null;
        save: (values: FileEditValues) => Promise<unknown>;
        /** Whether to offer the field-staff share toggle. */
        shareable?: boolean;
    }>(),
    { shareable: false },
);

const emit = defineEmits<{ saved: []; cancel: [] }>();

const isOpen = defineModel<boolean>({ default: false });

const { handleSubmit, isSubmitting, setErrors, setFieldValue } = useForm();

const seed = (file: Record<string, any> | null) => {
    if (!file) return;

    nextTick(() => {
        setFieldValue('display_label', file.custom_properties?.display_label || '');
        setFieldValue('tags', file.tags ? [...file.tags] : []);
        setFieldValue('shared_with_field_staff', file.shared_with_field_staff ?? false);
    });
};

watch(() => props.file, seed, { immediate: true });

watch(isOpen, (open) => {
    if (open) seed(props.file);
});

const onSubmit = handleSubmit(async (values) => {
    if (!props.file) return;

    try {
        await props.save({
            display_label: values.display_label || null,
            tags: values.tags ?? [],
            shared_with_field_staff: !!values.shared_with_field_staff,
        });

        toast.success('File updated');
        emit('saved');
        isOpen.value = false;
    } catch (error: any) {
        if (error?.response?.data?.errors) {
            setErrors(error.response.data.errors);
        }

        toast.error(error?.response?.data?.message || 'Could not update the file');
    }
});

const handleCancel = () => {
    emit('cancel');
    isOpen.value = false;
};
</script>
